import { useState } from "react";

export type NotePageItem = {
  id: string;
  title: string;
};

type NotePageListProps = {
  pages: NotePageItem[];
  activePageId: string | null;
  emptyMessage: string;
  sectionLabel: string;
  addAriaLabel: string;
  onSelect: (id: string) => void;
  onAdd: () => void;
  onRemove: (id: string) => void;
  onRename: (id: string, title: string) => void;
};

export default function NotePageList({
  pages,
  activePageId,
  emptyMessage,
  sectionLabel,
  addAriaLabel,
  onSelect,
  onAdd,
  onRemove,
  onRename,
}: NotePageListProps) {
  const [editingId, setEditingId] = useState<string | null>(null);
  const [draft, setDraft] = useState("");

  const commit = (id: string) => {
    onRename(id, draft || "새 페이지");
    setEditingId(null);
  };

  return (
    <div className="lg:w-56 flex-shrink-0">
      <div className="flex items-center justify-between mb-3 px-1">
        <p className="text-xs font-medium text-gray-400 uppercase tracking-wider">
          {sectionLabel}
        </p>
        <button
          onClick={onAdd}
          className="w-6 h-6 flex items-center justify-center bg-indigo-100 hover:bg-indigo-200 text-indigo-600 rounded transition-all cursor-pointer"
          aria-label={addAriaLabel}
        >
          <i className="ri-add-line text-sm" />
        </button>
      </div>

      <div className="flex lg:flex-col gap-2 overflow-x-auto lg:overflow-x-visible pb-2 lg:pb-0">
        {pages.length === 0 && (
          <p className="text-xs text-gray-400 px-1">{emptyMessage}</p>
        )}
        {pages.map((page) => {
          const isActive = activePageId === page.id;
          const isEditing = editingId === page.id;
          return (
            <div
              key={page.id}
              className={`group flex items-center gap-2 px-3 py-2.5 rounded-lg text-sm font-medium transition-all cursor-pointer flex-shrink-0 ${
                isActive
                  ? "bg-indigo-500 text-white shadow-lg"
                  : "bg-white text-gray-700 hover:bg-gray-100 border border-gray-200"
              }`}
              onClick={() => onSelect(page.id)}
            >
              {isEditing ? (
                <input
                  autoFocus
                  value={draft}
                  onChange={(e) => setDraft(e.target.value)}
                  onBlur={() => commit(page.id)}
                  onKeyDown={(e) => {
                    if (e.key === "Enter") commit(page.id);
                  }}
                  onClick={(e) => e.stopPropagation()}
                  className="flex-1 min-w-0 bg-transparent outline-none text-sm"
                />
              ) : (
                <span
                  className="flex-1 min-w-0 truncate"
                  onDoubleClick={(e) => {
                    e.stopPropagation();
                    setEditingId(page.id);
                    setDraft(page.title);
                  }}
                >
                  {page.title}
                </span>
              )}
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  onRemove(page.id);
                }}
                className={`opacity-0 group-hover:opacity-100 w-4 h-4 flex items-center justify-center rounded transition-all ${
                  isActive
                    ? "text-white/70 hover:text-white"
                    : "text-gray-400 hover:text-red-500"
                }`}
                aria-label="삭제"
              >
                <i className="ri-close-line text-xs" />
              </button>
            </div>
          );
        })}
      </div>
    </div>
  );
}
